import React, { useEffect, useRef } from 'react';
import useReveal from '../hooks/useReveal';
import './Trustpilot.css';

/* ─────────────────────────────────────────────────────────────
   TRUSTPILOT EMBED
   To connect your real Trustpilot profile:
   1. Log into business.trustpilot.com
   2. Go to Integrations → TrustBox widgets
   3. Copy your data-businessunit-id (looks like 64a1b2c3d4e5f6...)
   4. Replace the placeholder value below
   ───────────────────────────────────────────────────────────── */
const TRUSTPILOT_BUSINESS_ID = '69f1c3ebb469355fbcefeb91';

/* Static showcase cards — shown while Trustpilot widget loads,
   or as a designed fallback. Replace with your own real reviews. */
const reviews = [
  {
    id: 1,
    name: 'Taiwo A.',
    location: 'Liverpool',
    rating: 5,
    date: 'March 2026',
    service: 'End of Tenancy Cleaning',
    text: 'Absolutely spotless. The team went above and beyond. My landlord was genuinely impressed and I got my full deposit back. Cannot recommend Fresh Wave enough!',
  },
  {
    id: 2,
    name: 'James O.',
    location: 'Wavertree',
    rating: 5,
    date: 'February 2026',
    service: 'Office & Commercial Cleaning',
    text: 'Reliable, professional, and thorough. They come in after hours and the office always looks immaculate the next morning. Great value for money.',
  },
  {
    id: 3,
    name: 'Priya K.',
    location: 'Kensington',
    rating: 5,
    date: 'January 2026',
    service: 'Airbnb Cleaning',
    text: "I've been using Fresh Wave for my Airbnb for 6 months. Fast turnaround, always on time, and guests regularly comment on how clean the property is.",
  },
];

function StarRating({ rating }) {
  return (
    <div className="tp-stars" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <svg
          key={n}
          className={`tp-star ${n <= rating ? 'tp-star--filled' : ''}`}
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
}

function ReviewCard({ name, location, rating, date, service, text }) {
  const ref = useReveal();
  return (
    <div className="review-card" ref={ref}>
      <div className="review-card__header">
        <div className="review-card__avatar">
          {name.charAt(0)}
        </div>
        <div>
          <div className="review-card__name">{name}</div>
          <div className="review-card__meta">{location} · {date}</div>
        </div>
        <div className="review-card__tp-logo" aria-label="Trustpilot">
          <svg viewBox="0 0 126 126" fill="none">
            <path d="M63 0C28.2 0 0 28.2 0 63s28.2 63 63 63 63-28.2 63-63S97.8 0 63 0z" fill="#00B67A"/>
            <path d="M85.5 54H67.6L63 37.5 58.4 54H40.5l14.8 10.7-5.6 17.3L63 71.3l13.3 10.7-5.6-17.3L85.5 54z" fill="#fff"/>
          </svg>
        </div>
      </div>
      <StarRating rating={rating} />
      <p className="review-card__text">"{text}"</p>
      <div className="review-card__service">
        <span className="review-card__service-tag">{service}</span>
      </div>
    </div>
  );
}

/* Official Trustpilot TrustBox widget */
function TrustpilotWidget() {
  const widgetRef = useRef(null);

  useEffect(() => {
    if (window.Trustpilot && widgetRef.current) {
      window.Trustpilot.loadFromElement(widgetRef.current, true);
    }
  }, []);

  return (
    <div
      ref={widgetRef}
      className="trustpilot-widget"
      data-locale="en-GB"
      data-template-id="56278e9abfbbba0bdcd568bc"   /* "Micro Review Count" template */
      data-businessunit-id={TRUSTPILOT_BUSINESS_ID}
      data-style-height="60px"
      data-style-width="100%"
      data-theme="light"
      data-token="e9a8c09a-35a0-46e6-8547-166233de1ad2"
    >
      <a
        href={`https://uk.trustpilot.com/review/${TRUSTPILOT_BUSINESS_ID}`}
        target="_blank"
        rel="noopener noreferrer"
        className="tp-fallback-link"
      >
        See our reviews on Trustpilot
      </a>
    </div>
  );
}

export default function Trustpilot() {
  const headerRef = useReveal();

  return (
    <section className="trustpilot-section" id="reviews">
      {/* Section header */}
      <div className="tp-header" ref={headerRef}>
        <span className="section-label">What Our Clients Say</span>
        <h2 className="section-title">
          Trusted by Homes &amp; <em>Businesses</em>
        </h2>
        <p className="tp-header__sub">
          Real reviews from real clients across Liverpool and beyond.
        </p>

        {/* Official Trustpilot embed */}
        <div className="tp-widget-wrap">
          <TrustpilotWidget />
        </div>
      </div>

      {/* Designed review cards */}
      <div className="reviews-grid">
        {reviews.map((r) => <ReviewCard key={r.id} {...r} />)}
      </div>

      {/* CTA to Trustpilot */}
      <div className="tp-cta">
        <a
          href="https://uk.trustpilot.com/review/freshwavesolutionsltd.co.uk"
          target="_blank"
          rel="noopener noreferrer"
          className="tp-cta__link"
        >
          <svg className="tp-cta__logo" viewBox="0 0 126 126" fill="none">
            <path d="M63 0C28.2 0 0 28.2 0 63s28.2 63 63 63 63-28.2 63-63S97.8 0 63 0z" fill="#00B67A"/>
            <path d="M85.5 54H67.6L63 37.5 58.4 54H40.5l14.8 10.7-5.6 17.3L63 71.3l13.3 10.7-5.6-17.3L85.5 54z" fill="#fff"/>
          </svg>
          Read all reviews on Trustpilot
        </a>
      </div>
    </section>
  );
}
